import { forwardRef } from 'react';

import { Box, Flex, Input, InputGroup, Skeleton, Text } from '@chakra-ui/react';

import { RANGE_PICKER } from '@nocowanie/core/consts';
import { dateHelpers } from '@nocowanie/core/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';
import { IconsNocowanie } from '@nocowanie/icons/nocowanie';

import { defaultRangePickerConfig, RangePickerConfig } from './range-picker.config';
import { RangePickerInputProps } from './range-picker-input.props';

import { getFlexDateLabel } from '../../../helpers/range-picker-flex-dates.helpers';

export const RangePickerInput = forwardRef<HTMLInputElement, any>(
    (
        {
            config,
            displayedValue,
            isDrawerPending,
            onClick,
            displayedDateFormat = 'dd MMM',
            inputProps,
            invalid,
            flexDates,
            ...rest
        }: RangePickerInputProps,
        ref,
    ) => {
        const { translationData }: Required<RangePickerConfig> = {
            ...defaultRangePickerConfig,
            ...config,
        };

        const {
            withFlexDates = false,
            selectedFlexDays = RANGE_PICKER.FLEX_DATES.DEFAULT,
            hideFlexDaysLabel = false,
        } = flexDates || {};

        const { css: inputPropsCss, ...restInputProps } = inputProps || {};
        // Input and fake label needs common styling
        const inputStyles = {
            border: inputProps?.border ?? '1px solid {colors.border}',
            borderRadius: inputProps?.borderRadius ?? 'md',
            backgroundColor: inputProps?.backgroundColor ?? 'white',
            color: invalid ? 'red.solid' : (inputProps?.backgroundColor ?? undefined),
            ...inputPropsCss,
        };

        const startDate = displayedValue?.startDate
            ? dateHelpers.format(displayedValue?.startDate, displayedDateFormat)
            : undefined;
        const endDate = displayedValue?.endDate
            ? dateHelpers.format(displayedValue?.endDate, displayedDateFormat)
            : undefined;
        const startDateFormatted = displayedValue?.startDate
            ? dateHelpers.format(displayedValue?.startDate, RANGE_PICKER.DATE_FORMAT)
            : '';
        const endDateFormatted = displayedValue?.endDate
            ? dateHelpers.format(displayedValue?.endDate, RANGE_PICKER.DATE_FORMAT)
            : '';

        const shouldShowFlexDatesLabel =
            withFlexDates &&
            selectedFlexDays > RANGE_PICKER.FLEX_DATES.DEFAULT &&
            !hideFlexDaysLabel;
        const flexDatesLabel = shouldShowFlexDatesLabel
            ? getFlexDateLabel(selectedFlexDays, translationData)
            : '';

        return (
            <InputGroup
                onClick={onClick}
                data-testid={'range-picker-input'}
                data-value={`${startDateFormatted} - ${endDateFormatted}`}
                startElement={
                    <IconsLinear.Calendar
                        size={'md'}
                        color={invalid ? 'red.solid' : 'secondary.solid'}
                    />
                }
                {...rest}
            >
                <Box width={'100%'}>
                    <Input
                        value={`${startDate} - ${endDate}`}
                        readOnly
                        ref={ref}
                        css={{
                            ...inputStyles,
                            cursor: 'pointer',
                            // _focusVisible: {
                            //     outlineWidth: 0,
                            //     boxShadow: 'none',
                            // }, // Focus styles override
                            _readOnly: {
                                userSelect: 'none', // Fix for iOS safari issue with not clickable parts of input (mainly text inside)
                                boxShadow: 'none',
                            },
                        }}
                        aria-label={translationData.mobileLabel}
                        {...restInputProps}
                        disabled={isDrawerPending}
                    />

                    {/* Fake label - covers the input */}
                    <Flex
                        gap={2}
                        position={'absolute'}
                        alignItems={'center'}
                        pl={{ base: 10, md: 12 }}
                        left={0}
                        right={0}
                        top={0}
                        bottom={0}
                        pointerEvents={'none'}
                        backgroundClip={'padding-box'}
                        whiteSpace={'nowrap'}
                        aria-hidden="true"
                        css={{
                            ...inputStyles,
                            borderColor: 'transparent',
                        }}
                    >
                        {isDrawerPending ? (
                            <Skeleton height={'20px'} width={'90%'} />
                        ) : (
                            <>
                                {startDate && endDate ? (
                                    <>
                                        {startDate}
                                        <Box>
                                            <IconsLinear.ArrowRight
                                                mt={-1}
                                                color={invalid ? 'red.solid' : 'secondary.solid'}
                                            />
                                        </Box>
                                        {endDate}
                                        {shouldShowFlexDatesLabel && (
                                            <Text as={'span'} textTransform={'lowercase'}>
                                                (
                                                <IconsNocowanie.PlusMinus
                                                    scale={0.6}
                                                    ml={-1}
                                                    mr={-1.5}
                                                />{' '}
                                                {flexDatesLabel})
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    translationData.dateInputPlaceholder
                                )}
                            </>
                        )}
                    </Flex>
                </Box>
            </InputGroup>
        );
    },
);
